// src/context/AuthProvider.jsx
import React, { createContext, useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setUsername, setRequiresPasswordChange, setAuthSession, clearAuthSession } from '../redux/slices/Auth/login'
import { isUserVerify, getUserById, getEventsPendingReview, extendPublicEventDate } from '../services'
import { loadStoredUser, TOKEN_STORAGE_KEY } from '../utils/authStorage'
import { hasPermissionFlags } from '../utils/treePermissions'
import { fetchNotifications } from '../redux/slices/Notifications/notificationsSlice'
import { invalidateMapData } from '../redux/slices/Tree/treeFeatures'
import { clearFilteredData, setEnabledFilter } from '../redux/slices/Search/input'
import ForceChangePasswordModal from '../components/ForceChangePasswordModal'
import EventEndedAlertModal from '../components/EventEndedAlertModal'
import { isMaintenancePayload, isMaintenanceResponse } from '../utils/maintenance'

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch()
  const authenticated = useSelector(state => state.login.authenticated)
  const requiresPasswordChange = useSelector(state => state.login.requiresPasswordChange)
  // Render optimista: si ya hay sesión hidratada, no bloqueamos con loading
  // y revalidamos en segundo plano. Solo bloqueamos cuando hay token pero
  // falta el usuario persistido (sesión incompleta / login previo a este cambio),
  // o cuando sus asignaciones no traen los flags de permisos (sesión guardada antes de ellos).
  const [loading, setLoading] = useState(() => {
    const hasToken = !!window.localStorage.getItem(TOKEN_STORAGE_KEY)
    const storedUser = loadStoredUser()
    const hasHydratedUser = !!storedUser
    const hasStoredPermissionFlags = hasPermissionFlags(storedUser?.user?.assignedTenants)
    return hasToken && !(hasHydratedUser && hasStoredPermissionFlags)
  })
  const [loadingStage, setLoadingStage] = useState('Verificando sesión...')
  const hasInitialized = useRef(false)
  const [pendingEvents, setPendingEvents] = useState([])
  const [showEventAlert, setShowEventAlert] = useState(false)

  const recheckPendingEvents = useCallback(async () => {
    const events = await getEventsPendingReview()
    setPendingEvents(events)
    if (events.length > 0) setShowEventAlert(true)
    dispatch(fetchNotifications())
  }, [dispatch])

  useEffect(() => {
    // Evitar re-verificación innecesaria
    if (hasInitialized.current) return
    hasInitialized.current = true

    const verifyAuth = async () => {
      const token = window.localStorage.getItem(TOKEN_STORAGE_KEY)

      if (!token) {
        // Limpia cualquier usuario huérfano si el token ya no está.
        dispatch(clearAuthSession())
        setLoading(false)
        return
      }

      try {
        setLoadingStage('Verificando credenciales...')
        const userLocal = await isUserVerify(`Bearer ${token}`)

        if (isMaintenancePayload(userLocal)) return

        if (!userLocal?.auth) {
          dispatch(clearAuthSession())
          setLoading(false)
          return
        }

        dispatch(setAuthSession(userLocal))
        if (userLocal.contrasena_temporal) dispatch(setRequiresPasswordChange(true))
        setLoadingStage('Cargando perfil...')

        // Obtener datos de usuario (opcional, puede ser diferido)
        try {
          const res = await getUserById(userLocal.user.id, `Bearer ${token}`)
          const user = res.user
          let username = `${user?.nombre} ${user?.apellido}`
          username =
            username.length > 18 ? `${user?.nombre[0]} ${user?.apellido}` : username
          dispatch(setUsername(username))
        } catch (err) {
          console.warn('Error al cargar datos de usuario:', err)
          dispatch(setUsername('Usuario'))
        }

        // Verificar eventos de prueba vencidos pendientes de atención
        try {
          const events = await getEventsPendingReview()
          if (events.length > 0) {
            setPendingEvents(events)
            setShowEventAlert(true)
          }
        } catch (err) {
          console.warn('Error al verificar eventos pendientes:', err)
        }

        // Cargar notificaciones del usuario
        dispatch(fetchNotifications())
      } catch (error) {
        if (isMaintenanceResponse(error) || isMaintenancePayload(error?.response?.data)) {
          return
        }
        console.error('Error en verificación:', error)
        dispatch(clearAuthSession())
      } finally {
        setLoading(false)
      }
    }

    verifyAuth()
  }, [dispatch])

  // Re-verificar al volver al tab si el usuario está autenticado
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && authenticated?.auth) {
        recheckPendingEvents()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [authenticated?.auth, recheckPendingEvents])

  // Polling de notificaciones cada 60s mientras la sesión está activa
  useEffect(() => {
    if (!authenticated?.auth) return
    const interval = setInterval(() => dispatch(fetchNotifications()), 60 * 1000)
    return () => clearInterval(interval)
  }, [authenticated?.auth, dispatch])

  const handleExtendEvent = useCallback(async (eventId, newEndDate) => {
    await extendPublicEventDate(eventId, newEndDate)
    const events = await getEventsPendingReview()
    setPendingEvents(events)
    if (events.length === 0) setShowEventAlert(false)
  }, [])

  const handlePostCleanEvent = useCallback(async () => {
    dispatch(setEnabledFilter(false))
    dispatch(clearFilteredData())
    dispatch(invalidateMapData())
    const events = await getEventsPendingReview()
    setPendingEvents(events)
    if (events.length === 0) setShowEventAlert(false)
  }, [dispatch])

  const handleDismissAlert = useCallback(() => {
    setShowEventAlert(false)
  }, [])

  const isAuthenticated = !!authenticated?.auth
  const roleUser = authenticated?.user?.rol
  const isAdmin = roleUser?.includes('administrador')
  const isResearcher = roleUser?.includes('investigador')

  // Tenants asignados al investigador (del backend)
  const assignedTenants = useMemo(
    () => authenticated?.user?.assignedTenants || [],
    [authenticated?.user?.assignedTenants],
  )

  // Cantones donde el usuario puede editar/registrar
  // Admin sin asignaciones = superadmin (null = acceso total)
  const allowedCantonSlugs = useMemo(() => {
    if (isAdmin && assignedTenants.length === 0) return null
    return [...new Set(assignedTenants.map(t => t.cantonSlug))]
  }, [isAdmin, assignedTenants])

  // Cantón primario del usuario (para redirecciones)
  const primaryCantonSlug = useMemo(() => {
    if (allowedCantonSlugs === null) return null
    const primary = assignedTenants.find(t => t.isPrimary)
    return primary?.cantonSlug || assignedTenants[0]?.cantonSlug || null
  }, [allowedCantonSlugs, assignedTenants])

  // Obtener tenants asignados para un cantón específico
  const getAssignedTenantsForCanton = useCallback(
    cantonSlug => {
      if (allowedCantonSlugs === null) return null // null = usar todos del contexto
      return assignedTenants.filter(t => t.cantonSlug === cantonSlug || t.isGeneric)
    },
    [allowedCantonSlugs, assignedTenants],
  )

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAdmin,
        isResearcher,
        user: authenticated?.user,
        loading,
        loadingStage,
        assignedTenants,
        allowedCantonSlugs,
        primaryCantonSlug,
        getAssignedTenantsForCanton,
      }}>
      {children}
      {requiresPasswordChange && <ForceChangePasswordModal />}
      {showEventAlert && pendingEvents.length > 0 && (
        <EventEndedAlertModal
          events={pendingEvents}
          onExtend={handleExtendEvent}
          onCleanDone={handlePostCleanEvent}
          onDismiss={handleDismissAlert}
        />
      )}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
